import { useQueries } from "@tanstack/react-query";
import { IUserSessionData } from "api/model/user/IUserSessionData";
import { UserDataApi } from "api/UserDataApi";
import { IUserStatisticsComponentProps, secondsToHm } from "components/pages/users-statistics-dashboard/stats-utils";
import * as d3 from "d3";
import { ProgressSpinner } from "primereact/progressspinner";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { DATE_PATTERN_SECONDS } from "utils/constants";
import { fmtDatetime, toNativeDateOrNow } from "utils/time-utils";
import { useTimezone } from "hooks/useTimezone";

const CHART_MAX_HEIGHT = 600;
const BAR_HEIGHT = 5;
const CHART_MARGIN = { top: 0, right: 10, bottom: 20, left: 20 };
const MIN_BAR_WIDTH = 5;
const MULTIPLIER_FOR_MARGIN = 5;

const UserStatisticsGanttChart = ({
  userSessionData,
  groupBy,
  companyFilter,
  jobTitleFilter,
}: IUserStatisticsComponentProps) => {
  const tz = useTimezone();
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef(null);
  const [ganttChartData, setGanttChartData] = useState<IUserSessionData[]>([]);

  const uniqueUsernames: Set<string> = new Set();
  userSessionData.map(session => {
    uniqueUsernames.add(session.username);
  });
  let uniqueUsernamesArray: string[] = [...uniqueUsernames];

  const userQueries = useQueries({
    queries: uniqueUsernamesArray.map(user => ({
      queryKey: ["user", user],
      queryFn: () => UserDataApi.getDataInfo(user),
    })),
    combine: results => {
      return {
        data: results.map(result => result.data),
        pending: results.some(result => result.isPending),
      };
    },
  });

  const usersInfo = useMemo(() => {
    if (userQueries.pending) {
      return [];
    }
    return userQueries.data.map(query => query?.data);
  }, [userQueries]);

  // TODO do it in a not so hacky way
  useEffect(() => {
    if (groupBy === "jobTitle") {
      let tempData = structuredClone(userSessionData);
      tempData.forEach((user: any) => {
        if (user.jobTitle === undefined) {
          user["jobTitle"] = "No Title";
        }
        user["displayName"] = user.jobTitle;
      });
      if (jobTitleFilter?.length === 0) {
        tempData = tempData.filter(data => data.jobTitle === "No Title");
      }

      setGanttChartData(tempData);
    }

    if (groupBy === "username") {
      const tempData = userSessionData.map(data => {
        const matchingUser = usersInfo.find(query => data.username === query?.username);

        if (matchingUser) {
          const temp = { ...data };
          temp.displayName = matchingUser.displayName;
          return temp;
        } else {
          return data;
        }
      });
      setGanttChartData(tempData);
    }
  }, [usersInfo, groupBy, companyFilter, jobTitleFilter, userSessionData]);

  function tooltipHandler(event: any, d: IUserSessionData) {
    // Add mouseover event
    const sessionTime = (toNativeDateOrNow(d.to).getTime() - toNativeDateOrNow(d.from).getTime()) / 1000; // Convert to seconds
    const sessionTimeFormat = secondsToHm(sessionTime); // Format to HH:mm:ss
    const startTimeFormat = fmtDatetime(d.from, tz.display, DATE_PATTERN_SECONDS);
    const endTimeFormat = fmtDatetime(d.to, tz.display, DATE_PATTERN_SECONDS);
    d3.select(tooltipRef.current)
      .style("left", event.pageX + "px")
      .style("top", event.pageY + "px")
      .style("opacity", 1)
      .html(
        `User: ${d.displayName}<br>Start: ${startTimeFormat}<br>End: ${endTimeFormat}<br>Session Time: ${sessionTimeFormat}`
      );
  }

  useEffect(() => {
    // Set up chart dimensions
    const maxUsernameLength = Math.max(
      ...ganttChartData.map(user => {
        return user.displayName?.length || user.username.length;
      })
    );
    const maxLeftMargin = CHART_MARGIN.left + maxUsernameLength * MULTIPLIER_FOR_MARGIN;
    const width = (svgRef.current?.parentElement?.offsetWidth ?? 0) - maxLeftMargin - CHART_MARGIN.right;
    const totalHeight = ganttChartData.length * BAR_HEIGHT;
    const actualHeight = totalHeight < CHART_MAX_HEIGHT ? totalHeight : CHART_MAX_HEIGHT;
    const height = actualHeight - CHART_MARGIN.top - CHART_MARGIN.bottom;

    // Create SVG container
    const svgContainer = d3
      .select(svgRef.current)
      .attr("width", width + maxLeftMargin + CHART_MARGIN.right)
      .attr("height", height + CHART_MARGIN.top + CHART_MARGIN.bottom);

    svgContainer.on("dblclick", () => {
      if (minDate !== undefined && maxDate !== undefined) {
        xScale.domain([minDate, maxDate]);
      }

      svg.select(".xAxis").call(xAxis as any);
      barsLayer
        .selectAll<SVGRectElement, IUserSessionData>("rect")
        .attr("x", (d: IUserSessionData) => xScale(toNativeDateOrNow(d.from) as Date))
        .attr("width", (d: IUserSessionData) =>
          Math.max(xScale(toNativeDateOrNow(d.to) as Date) - xScale(toNativeDateOrNow(d.from) as Date), MIN_BAR_WIDTH)
        );
    });

    const svg = svgContainer.append("g").attr("transform", `translate(${maxLeftMargin},${CHART_MARGIN.top})`);

    let xScale = d3.scaleTime().domain([new Date(), new Date()]).range([0, width]);

    const yScale = d3
      .scaleBand()
      .domain(ganttChartData.map(d => d.displayName ?? d.username))
      .range([0, height])
      .padding(0.1);

    let minDate = d3.min(ganttChartData, (d: IUserSessionData) => toNativeDateOrNow(d.from));
    let maxDate = d3.max(ganttChartData, (d: IUserSessionData) => toNativeDateOrNow(d.to));

    if (minDate !== undefined && maxDate !== undefined) {
      xScale = d3.scaleTime().domain([minDate, maxDate]).range([0, width]);
    }

    // Define color scale
    const colorScale = d3
      .scaleOrdinal(d3.schemeCategory10)
      .domain(ganttChartData.map((d: IUserSessionData) => d.displayName ?? d.username));

    const brushLayer = svg.append("g").attr("class", "brush-layer");
    const barsLayer = svg.append("g").attr("class", "bars-layer");

    // Draw Gantt bars
    barsLayer
      .selectAll("rect")
      .data(ganttChartData as IUserSessionData[])
      .enter()
      .append("rect")
      .attr("x", d => xScale(toNativeDateOrNow(d.from) as any))
      .attr("y", d => yScale(d.displayName as string) || 0)
      .attr("width", d => {
        return Math.max(xScale(toNativeDateOrNow(d.to) as Date) - xScale(toNativeDateOrNow(d.from) as Date), MIN_BAR_WIDTH);
      })
      .attr("height", yScale.bandwidth())
      .attr("fill", d => (isSessionActive(d) ? "lightgrey" : colorScale(d.displayName as string)))
      .on("mouseover", tooltipHandler)
      .on("mouseout", function () {
        d3.select(tooltipRef.current).style("opacity", 0);
      });

    // Draw white background to the left of Y-axis
    svg
      .append("rect")
      .attr("x", 0 - maxLeftMargin)
      .attr("y", 0)
      .attr("width", maxLeftMargin)
      .attr("height", height + CHART_MARGIN.bottom)
      .attr("fill", "#fff");

    // Draw axes
    const xAxis = d3.axisBottom(xScale);
    const yAxis = d3.axisLeft(yScale);

    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .attr("class", "xAxis")
      .call(xAxis as any);

    svg
      .append("g")
      .attr("class", "yAxis")
      .call(yAxis as any);

    let brush = d3
      .brushX()
      .extent([
        [0, 0],
        [width, height],
      ])
      .on("end", brushed);

    brushLayer.call(brush);

    function brushed(event: any) {
      if (event.selection) {
        let extent = event.selection;

        // Update xScale domain based on selected area
        xScale.domain([xScale.invert(extent[0]), xScale.invert(extent[1])]);

        // Redraw the chart elements with updated scales
        svg.select(".xAxis").call(xAxis as any);
        barsLayer
          .selectAll<SVGRectElement, IUserSessionData>("rect")
          .attr("x", (d: IUserSessionData) => xScale(toNativeDateOrNow(d.from) as Date))
          .attr("width", (d: IUserSessionData) =>
            Math.max(xScale(toNativeDateOrNow(d.to) as Date) - xScale(toNativeDateOrNow(d.from) as Date), MIN_BAR_WIDTH)
          );

        // Clear the brush selection
        brushLayer.call(brush.move, null);
      }
    }

    function isSessionActive(session: IUserSessionData) {
      const now = new Date();
      return toNativeDateOrNow(session.from) <= now && toNativeDateOrNow(session.to) >= now;
    }

    // Cleanup on component unmount
    return () => {
      svg.selectAll("*").remove();
    };
  }, [groupBy, companyFilter, jobTitleFilter, ganttChartData]);

  return (
    <div>
      {usersInfo.length < uniqueUsernamesArray.length ? (
        <div className="flex justify-content-center align-items-center">
          <ProgressSpinner />
        </div>
      ) : (
        <>
          <svg ref={svgRef} />
          <div
            ref={tooltipRef}
            style={{
              position: "absolute",
              zIndex: 10,
              opacity: 0,
              backgroundColor: "white",
              color: "black",
              padding: "2px",
              borderRadius: "2px",
              pointerEvents: "none",
              transform: "translate(-0, -70px)",
            }}
          />
        </>
      )}
    </div>
  );
};

export default UserStatisticsGanttChart;